import { useState, useRef, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Sparkles, Globe, Loader2, Building2, CheckCircle2, Edit3, Save, X, Pencil, Search, FileSearch, Brain, Bot, Check, Phone, Languages, UserCircle, Mic, PhoneCall, Shield, Settings } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { toast as sonnerToast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";
interface UrlAnalyzerProps {
  onComplete: (config: any) => void;
  onBack: () => void;
  initialQuery?: string;
}
type AnalysisStep = 'idle' | 'fetching' | 'analyzing' | 'building' | 'complete';

const ANALYSIS_STEPS = [
{ id: 'fetching', icon: FileSearch, labelKey: 'urlToAgent.fetchingContent', searchLabelKey: 'urlToAgent.searchingInfo' },
{ id: 'analyzing', icon: Brain, labelKey: 'urlToAgent.analyzingAI', searchLabelKey: 'urlToAgent.analyzingAI' },
{ id: 'building', icon: Bot, labelKey: 'urlToAgent.buildingAgent', searchLabelKey: 'urlToAgent.buildingAgent' }] as
const;

const UrlAnalyzer = ({
  onComplete,
  onBack,
  initialQuery
}: UrlAnalyzerProps) => {
  const [url, setUrl] = useState(initialQuery || "");
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState<AnalysisStep>('idle');
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [analyzedConfig, setAnalyzedConfig] = useState<any>(null);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<any>("");
  const [isHighlighted, setIsHighlighted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStepIndex, setSaveStepIndex] = useState(0);
  const {
    toast
  } = useToast();
  const {
    t
  } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  const SAVE_STEPS = [
    { icon: Languages, labelKey: 'urlToAgent.saveSteps.language' },
    { icon: UserCircle, labelKey: 'urlToAgent.saveSteps.gender' },
    { icon: Mic, labelKey: 'urlToAgent.saveSteps.voice' },
    { icon: PhoneCall, labelKey: 'urlToAgent.saveSteps.phone' },
    { icon: Shield, labelKey: 'urlToAgent.saveSteps.security' },
    { icon: Settings, labelKey: 'urlToAgent.saveSteps.finalizing' },
  ];

  useEffect(() => {
    if (!isSaving) {
      setSaveStepIndex(0);
      return;
    }
    const interval = setInterval(() => {
      setSaveStepIndex((prev) => (prev < SAVE_STEPS.length - 1 ? prev + 1 : prev));
    }, 2500);
    return () => clearInterval(interval);
  }, [isSaving]);

  // Auto-trigger analysis when initialQuery is provided
  const initialQueryTriggered = useRef(false);
  useEffect(() => {
    if (initialQuery && !initialQueryTriggered.current) {
      initialQueryTriggered.current = true;
      // Small delay to ensure component is mounted
      setTimeout(() => handleAnalyze(), 100);
    }
  }, [initialQuery]);
  const handleFeatureClick = () => {
    // Scroll to input
    inputRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });

    // Focus input
    inputRef.current?.focus();

    // Trigger flicker effect
    setIsHighlighted(true);
    setTimeout(() => setIsHighlighted(false), 2000);
  };
  const startEdit = (field: string, currentValue: any) => {
    setEditingField(field);
    setEditValue(currentValue);
  };
  const cancelEdit = () => {
    setEditingField(null);
    setEditValue("");
  };
  const saveEdit = (field: string) => {
    const fields = field.split('.');
    const newConfig = {
      ...analyzedConfig
    };
    if (fields.length === 1) {
      newConfig[fields[0]] = editValue;
    } else if (fields.length === 2) {
      newConfig[fields[0]] = {
        ...newConfig[fields[0]],
        [fields[1]]: editValue
      };
    }
    setAnalyzedConfig(newConfig);
    setEditingField(null);
    setEditValue("");
    toast({
      title: "Updated",
      description: "Field updated successfully"
    });
  };
  const removeService = (index: number) => {
    const newConfig = {
      ...analyzedConfig
    };
    newConfig.businessInfo.services = newConfig.businessInfo.services.filter((_: any, i: number) => i !== index);
    setAnalyzedConfig(newConfig);
  };
  const addService = () => {
    if (!editValue.trim()) return;
    const newConfig = {
      ...analyzedConfig
    };
    if (!newConfig.businessInfo.services) {
      newConfig.businessInfo.services = [];
    }
    newConfig.businessInfo.services.push(editValue.trim());
    setAnalyzedConfig(newConfig);
    setEditValue("");
    setEditingField(null);
  };
  const removeCapability = (index: number) => {
    const newConfig = {
      ...analyzedConfig
    };
    newConfig.capabilities = newConfig.capabilities.filter((_: any, i: number) => i !== index);
    setAnalyzedConfig(newConfig);
  };
  const addCapability = () => {
    if (!editValue.trim()) return;
    const newConfig = {
      ...analyzedConfig
    };
    if (!newConfig.capabilities) {
      newConfig.capabilities = [];
    }
    newConfig.capabilities.push(editValue.trim());
    setAnalyzedConfig(newConfig);
    setEditValue("");
    setEditingField(null);
  };
  const isValidUrl = (input: string): boolean => {
    try {
      const url = new URL(input);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const normalizeUrl = (inputUrl: string): string => {
    const trimmed = inputUrl.trim();
    if (!trimmed) return trimmed;

    // If it looks like a URL without protocol, add https://
    if (trimmed.match(/^[\w-]+\.[\w.-]+/)) {
      return `https://${trimmed}`;
    }
    return trimmed;
  };

  const handleUrlBlur = () => {
    if (url) {
      const normalized = normalizeUrl(url);
      if (isValidUrl(normalized)) {
        setUrl(normalized);
      }
    }
  };

  const handleAnalyze = async () => {
    if (!url.trim()) {
      // Highlight input instead of showing error
      inputRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
      inputRef.current?.focus();
      setIsHighlighted(true);
      setTimeout(() => setIsHighlighted(false), 2000);
      return;
    }

    const normalizedInput = normalizeUrl(url);
    const inputIsUrl = isValidUrl(normalizedInput);

    if (inputIsUrl) {
      setUrl(normalizedInput);
    }

    setIsSearchMode(!inputIsUrl);
    setIsLoading(true);
    setCurrentStep('fetching');

    try {
      // Simulate step progression with minimum display times
      const stepPromise = new Promise<void>((resolve) => {
        setTimeout(() => {
          setCurrentStep('analyzing');
          setTimeout(() => {
            setCurrentStep('building');
            resolve();
          }, 1500);
        }, 1500);
      });

      const [data] = await Promise.all([
      supabase.functions.invoke('analyze-url', {
        body: inputIsUrl ?
        { url: normalizedInput } :
        { searchQuery: url.trim() }
      }).then(({ data, error }) => {
        if (error) throw error;
        return data;
      }),
      stepPromise]
      );

      if (!data.success) {
        throw new Error(data.error || 'Failed to analyze');
      }

      setCurrentStep('complete');

      // Brief pause to show completion
      await new Promise((resolve) => setTimeout(resolve, 500));

      toast({
        title: "Success!",
        description: data.sourceType === "website" ?
        "Agent configuration created from your website" :
        "Agent configuration created from search results"
      });

      // Show analysis results first
      setAnalyzedConfig(data.config);
    } catch (error) {
      console.error('Error analyzing:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to analyze",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
      setCurrentStep('idle');
    }
  };

  const handleSaveAndCall = useCallback(async () => {
    if (isSaving || !analyzedConfig) return;
    setIsSaving(true);

    try {
      // Create agent via intro-agent function
      const { data, error } = await supabase.functions.invoke('intro-agent', {
        body: { agentConfig: analyzedConfig },
      });

      if (error || !data?.success) {
        console.error('Error creating agent:', error || data);
        sonnerToast.error(t('urlToAgent.saveFailed'));
        setIsSaving(false);
        return;
      }

      const sid = data.sessionId as string;
      const aid = data.agentId as string;

      // Set cookie for cross-subdomain transfer
      const expirationDate = new Date();
      expirationDate.setTime(expirationDate.getTime() + (24 * 60 * 60 * 1000));
      const cookieDomain = window.location.hostname.endsWith('call24x7.ai') ? '; domain=.call24x7.ai' : '';
      document.cookie = `session_id=${sid}${cookieDomain}; expires=${expirationDate.toUTCString()}; path=/; secure; SameSite=None`;

      // Redirect to signup with session_id and agent_id
      const signupUrl = new URL('https://manage.call24x7.ai/signup');
      signupUrl.searchParams.set('session_id', sid);
      signupUrl.searchParams.set('agent_id', aid);
      window.location.href = signupUrl.toString();
    } catch (err) {
      console.error('Save error:', err);
      sonnerToast.error(t('urlToAgent.saveError'));
      setIsSaving(false);
    }
  }, [isSaving, analyzedConfig]);

  // Show results if we have analyzed config
  if (analyzedConfig) {
    return <div className="max-w-4xl mx-auto animate-fade-in px-4">
        <Button variant="ghost" onClick={() => setAnalyzedConfig(null)} className="mb-6 md:mb-8 text-muted-foreground hover:text-foreground">
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('onboarding.urlAnalyzer.tryAnother')}
        </Button>

        <div className="space-y-6">
          <div className="text-center space-y-4 mb-6 md:mb-8 px-2">
            <div className="inline-flex items-center justify-center w-14 h-14 md:w-16 md:h-16 rounded-full bg-primary/10">
              <CheckCircle2 className="w-7 h-7 md:w-8 md:h-8 text-primary" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground">
              {t('onboarding.urlAnalyzer.analysisComplete')}
            </h2>
            <p className="text-sm md:text-base text-muted-foreground px-4">
              {t('onboarding.urlAnalyzer.reviewEdit')}
            </p>
            {isSaving ? (
              <div className="w-full max-w-sm mx-auto space-y-3 py-2">
                {SAVE_STEPS.map((step, index) => {
                  const StepIcon = step.icon;
                  const isActive = index === saveStepIndex;
                  const isComplete = index < saveStepIndex;
                  return (
                    <div
                      key={step.labelKey}
                      className={`flex items-center gap-3 p-3 rounded-xl transition-all duration-500 ${
                        isActive ? 'bg-primary/10 border border-primary/30 scale-[1.02]' :
                        isComplete ? 'bg-primary/5 border border-primary/10 opacity-60' :
                        'opacity-30'
                      }`}
                    >
                      <div className={`relative w-9 h-9 rounded-full flex items-center justify-center transition-all duration-500 ${
                        isActive ? 'bg-primary text-primary-foreground' :
                        isComplete ? 'bg-primary/60 text-primary-foreground' :
                        'bg-muted text-muted-foreground'
                      }`}>
                        {isComplete ? <Check className="w-5 h-5" /> : <StepIcon className="w-5 h-5" />}
                        {isActive && <div className="absolute inset-0 rounded-full border-2 border-primary/50 animate-ping" />}
                      </div>
                      <span className={`text-sm font-medium ${isActive ? 'text-foreground' : 'text-muted-foreground'}`}>
                        {t(step.labelKey)}
                      </span>
                      {isActive && <Loader2 className="w-4 h-4 animate-spin text-primary ml-auto" />}
                    </div>
                  );
                })}
              </div>
            ) : (
              <Button onClick={handleSaveAndCall} size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground px-6 md:px-8 h-11 md:h-12">
                <Phone className="mr-2 h-5 w-5" />
                {t('onboarding.urlAnalyzer.saveAndCall')}
              </Button>
            )}
          </div>

          <Card className="bg-card/80 backdrop-blur-sm border-border p-6 space-y-6">
            {/* Agent Name & Description */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-primary">
                <Sparkles className="h-5 w-5" />
                <h3 className="font-semibold text-lg">Agent Configuration</h3>
              </div>
              <div className="space-y-4">
                {/* Agent Name */}
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Agent Name</p>
                  {editingField === 'name' ? <div className="flex gap-2">
                      <Input value={editValue} onChange={(e) => setEditValue(e.target.value)} className="flex-1" autoFocus />
                      <Button size="sm" onClick={() => saveEdit('name')} className="bg-primary">
                        <Save className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={cancelEdit}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div> : <div className="flex items-center justify-between group cursor-pointer hover:bg-muted/50 p-2 -m-2 rounded-md transition-colors" onClick={() => startEdit('name', analyzedConfig.name)}>
                      <p className="text-lg font-semibold text-foreground">{analyzedConfig.name}</p>
                      <Button size="sm" variant="ghost" className="opacity-50 group-hover:opacity-100 transition-opacity">
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </div>}
                </div>

                {/* Description */}
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Description</p>
                  {editingField === 'description' ? <div className="space-y-2">
                      <Textarea value={editValue} onChange={(e) => setEditValue(e.target.value)} className="min-h-[80px]" autoFocus />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => saveEdit('description')} className="bg-primary">
                          <Save className="h-4 w-4 mr-1" />
                          Save
                        </Button>
                        <Button size="sm" variant="outline" onClick={cancelEdit}>
                          Cancel
                        </Button>
                      </div>
                    </div> : <div className="flex items-start justify-between group cursor-pointer hover:bg-muted/50 p-2 -m-2 rounded-md transition-colors" onClick={() => startEdit('description', analyzedConfig.description)}>
                      <p className="text-foreground flex-1">{analyzedConfig.description}</p>
                      <Button size="sm" variant="ghost" className="opacity-50 group-hover:opacity-100 transition-opacity">
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </div>}
                </div>

                {/* Greeting */}
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Greeting Message</p>
                  {editingField === 'greeting' ? <div className="space-y-2">
                      <Textarea value={editValue} onChange={(e) => setEditValue(e.target.value)} className="min-h-[80px]" autoFocus />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => saveEdit('greeting')} className="bg-primary">
                          <Save className="h-4 w-4 mr-1" />
                          Save
                        </Button>
                        <Button size="sm" variant="outline" onClick={cancelEdit}>
                          Cancel
                        </Button>
                      </div>
                    </div> : <div className="flex items-start justify-between group cursor-pointer hover:bg-muted/50 p-2 -m-2 rounded-md transition-colors" onClick={() => startEdit('greeting', analyzedConfig.greeting)}>
                      <p className="text-foreground italic flex-1">"{analyzedConfig.greeting}"</p>
                      <Button size="sm" variant="ghost" className="opacity-50 group-hover:opacity-100 transition-opacity">
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </div>}
                </div>
              </div>
            </div>

            {/* Business Info */}
            {analyzedConfig.businessInfo && <div className="space-y-3 pt-4 border-t border-border">
                <div className="flex items-center gap-2 text-primary">
                  <Building2 className="h-5 w-5" />
                  <h3 className="font-semibold text-lg">Business Information</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Business Name */}
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Business Name</p>
                    {editingField === 'businessInfo.name' ? <div className="flex gap-2">
                        <Input value={editValue} onChange={(e) => setEditValue(e.target.value)} autoFocus />
                        <Button size="sm" onClick={() => saveEdit('businessInfo.name')} className="bg-primary">
                          <Save className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={cancelEdit}>
                          <X className="h-4 w-4" />
                        </Button>
                      </div> : <div className="flex items-center justify-between group cursor-pointer hover:bg-muted/50 p-2 -m-2 rounded-md transition-colors" onClick={() => startEdit('businessInfo.name', analyzedConfig.businessInfo.name)}>
                        <p className="text-foreground">{analyzedConfig.businessInfo.name}</p>
                        <Button size="sm" variant="ghost" className="opacity-50 group-hover:opacity-100 transition-opacity">
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </div>}
                  </div>

                  {/* Business Type */}
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Business Type</p>
                    {editingField === 'businessInfo.type' ? <div className="flex gap-2">
                        <Input value={editValue} onChange={(e) => setEditValue(e.target.value)} autoFocus />
                        <Button size="sm" onClick={() => saveEdit('businessInfo.type')} className="bg-primary">
                          <Save className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={cancelEdit}>
                          <X className="h-4 w-4" />
                        </Button>
                      </div> : <div className="flex items-center justify-between group cursor-pointer hover:bg-muted/50 p-2 -m-2 rounded-md transition-colors" onClick={() => startEdit('businessInfo.type', analyzedConfig.businessInfo.type)}>
                        <p className="text-foreground">{analyzedConfig.businessInfo.type}</p>
                        <Button size="sm" variant="ghost" className="opacity-50 group-hover:opacity-100 transition-opacity">
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </div>}
                  </div>

                  {/* Business Hours */}
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Business Hours</p>
                    {editingField === 'businessInfo.hours' ? <div className="flex gap-2">
                        <Input value={editValue} onChange={(e) => setEditValue(e.target.value)} autoFocus />
                        <Button size="sm" onClick={() => saveEdit('businessInfo.hours')} className="bg-primary">
                          <Save className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={cancelEdit}>
                          <X className="h-4 w-4" />
                        </Button>
                      </div> : <div className="flex items-center justify-between group cursor-pointer hover:bg-muted/50 p-2 -m-2 rounded-md transition-colors" onClick={() => startEdit('businessInfo.hours', analyzedConfig.businessInfo.hours || '')}>
                        <p className="text-foreground">{analyzedConfig.businessInfo.hours || 'Not specified'}</p>
                        <Button size="sm" variant="ghost" className="opacity-50 group-hover:opacity-100 transition-opacity">
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </div>}
                  </div>

                  {/* Contact Info */}
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Contact Info</p>
                    {editingField === 'businessInfo.contact' ? <div className="flex gap-2">
                        <Input value={editValue} onChange={(e) => setEditValue(e.target.value)} autoFocus />
                        <Button size="sm" onClick={() => saveEdit('businessInfo.contact')} className="bg-primary">
                          <Save className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={cancelEdit}>
                          <X className="h-4 w-4" />
                        </Button>
                      </div> : <div className="flex items-center justify-between group cursor-pointer hover:bg-muted/50 p-2 -m-2 rounded-md transition-colors" onClick={() => startEdit('businessInfo.contact', analyzedConfig.businessInfo.contact || '')}>
                        <p className="text-foreground">{analyzedConfig.businessInfo.contact || 'Not specified'}</p>
                        <Button size="sm" variant="ghost" className="opacity-50 group-hover:opacity-100 transition-opacity">
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </div>}
                  </div>
                </div>
                
                {/* Services */}
                {analyzedConfig.businessInfo.services && <div>
                    <p className="text-sm text-muted-foreground mb-2">Services</p>
                    <div className="flex flex-wrap gap-2">
                      {analyzedConfig.businessInfo.services.map((service: string, idx: number) => <span key={idx} className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm flex items-center gap-1 group/service">
                          {service}
                          <button onClick={() => removeService(idx)} className="opacity-0 group-hover/service:opacity-100 transition-opacity ml-1">
                            <X className="h-3 w-3" />
                          </button>
                        </span>)}
                      {editingField === 'businessInfo.services.add' ? <div className="flex gap-1">
                          <Input value={editValue} onChange={(e) => setEditValue(e.target.value)} placeholder={t('urlToAgent.newService')} className="h-7 text-sm" autoFocus onKeyPress={(e) => e.key === 'Enter' && addService()} />
                          <Button size="sm" onClick={addService} className="h-7 px-2">
                            <Save className="h-3 w-3" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={cancelEdit} className="h-7 px-2">
                            <X className="h-3 w-3" />
                          </Button>
                        </div> : <button onClick={() => startEdit('businessInfo.services.add', '')} className="px-3 py-1 border border-dashed border-primary/50 text-primary rounded-full text-sm hover:bg-primary/5 transition-colors">
                          + Add Service
                        </button>}
                    </div>
                  </div>}
              </div>}

            {/* Capabilities */}
            {analyzedConfig.capabilities && <div className="space-y-3 pt-4 border-t border-border">
                <div className="flex items-center gap-2 text-primary">
                  <CheckCircle2 className="h-5 w-5" />
                  <h3 className="font-semibold text-lg">Agent Capabilities</h3>
                </div>
                <ul className="space-y-2">
                  {analyzedConfig.capabilities.map((capability: string, idx: number) => <li key={idx} className="flex items-start gap-2 text-foreground group/capability">
                      <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <span className="flex-1">{capability}</span>
                      <button onClick={() => removeCapability(idx)} className="opacity-0 group-hover/capability:opacity-100 transition-opacity">
                        <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                      </button>
                    </li>)}
                  {editingField === 'capabilities.add' ? <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <div className="flex-1 flex gap-2">
                        <Input value={editValue} onChange={(e) => setEditValue(e.target.value)} placeholder={t('urlToAgent.newCapability')} className="flex-1" autoFocus onKeyPress={(e) => e.key === 'Enter' && addCapability()} />
                        <Button size="sm" onClick={addCapability} className="bg-primary">
                          <Save className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={cancelEdit}>
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </li> : <li>
                      <button onClick={() => startEdit('capabilities.add', '')} className="text-primary hover:text-primary/80 text-sm flex items-center gap-1">
                        + Add Capability
                      </button>
                    </li>}
                </ul>
              </div>}

            {/* Knowledge Base */}
            {analyzedConfig.knowledgeBase && <div className="space-y-3 pt-4 border-t border-border">
                <div className="flex items-center gap-2 text-primary">
                  <Globe className="h-5 w-5" />
                  <h3 className="font-semibold text-lg">Knowledge Base</h3>
                </div>
                {editingField === 'knowledgeBase' ? <div className="space-y-2">
                    <Textarea value={editValue} onChange={(e) => setEditValue(e.target.value)} className="min-h-[150px] text-sm" autoFocus />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => saveEdit('knowledgeBase')} className="bg-primary">
                        <Save className="h-4 w-4 mr-1" />
                        Save
                      </Button>
                      <Button size="sm" variant="outline" onClick={cancelEdit}>
                        Cancel
                      </Button>
                    </div>
                  </div> : <div className="group relative cursor-pointer" onClick={() => startEdit('knowledgeBase', analyzedConfig.knowledgeBase)}>
                    <div className="bg-muted/50 p-4 rounded-lg hover:bg-muted/70 transition-colors">
                      <p className="text-foreground text-sm leading-relaxed">
                        {analyzedConfig.knowledgeBase}
                      </p>
                    </div>
                    <Button size="sm" variant="ghost" className="absolute top-2 right-2 opacity-50 group-hover:opacity-100 transition-opacity">
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </div>}
              </div>}
          </Card>

          <div className="space-y-4 pt-4">
            {isSaving ? (
              <div className="w-full max-w-sm mx-auto space-y-3 py-2 px-4">
                {SAVE_STEPS.map((step, index) => {
                  const StepIcon = step.icon;
                  const isActive = index === saveStepIndex;
                  const isComplete = index < saveStepIndex;
                  return (
                    <div
                      key={step.labelKey}
                      className={`flex items-center gap-3 p-3 rounded-xl transition-all duration-500 ${
                        isActive ? 'bg-primary/10 border border-primary/30 scale-[1.02]' :
                        isComplete ? 'bg-primary/5 border border-primary/10 opacity-60' :
                        'opacity-30'
                      }`}
                    >
                      <div className={`relative w-9 h-9 rounded-full flex items-center justify-center transition-all duration-500 ${
                        isActive ? 'bg-primary text-primary-foreground' :
                        isComplete ? 'bg-primary/60 text-primary-foreground' :
                        'bg-muted text-muted-foreground'
                      }`}>
                        {isComplete ? <Check className="w-5 h-5" /> : <StepIcon className="w-5 h-5" />}
                        {isActive && <div className="absolute inset-0 rounded-full border-2 border-primary/50 animate-ping" />}
                      </div>
                      <span className={`text-sm font-medium ${isActive ? 'text-foreground' : 'text-muted-foreground'}`}>
                        {t(step.labelKey)}
                      </span>
                      {isActive && <Loader2 className="w-4 h-4 animate-spin text-primary ml-auto" />}
                    </div>
                  );
                })}
              </div>
            ) : (
              <>
                <div className="text-center">
                  <p className="text-sm md:text-base text-muted-foreground mb-3 px-4">
                    {t('onboarding.urlAnalyzer.saveCallDescription')}
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 justify-center items-center px-4">
                  <Button onClick={handleSaveAndCall} size="lg" disabled={isSaving} className="bg-primary hover:bg-primary/90 text-primary-foreground px-6 md:px-8 h-11 md:h-12 w-full sm:w-auto">
                    <Phone className="mr-2 h-5 w-5" />
                    {t('onboarding.urlAnalyzer.saveAndCall')}
                  </Button>
                  <button onClick={() => setAnalyzedConfig(null)} className="text-sm text-muted-foreground hover:text-primary transition-colors underline underline-offset-4">
                    {t('onboarding.urlAnalyzer.analyzeDifferentUrl')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>;
  }
  return <div className="max-w-4xl mx-auto animate-fade-in px-4">
      <div className="text-center space-y-6 md:space-y-8">
        

        <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto px-2">
          {t('onboarding.urlAnalyzer.subtitle')}
        </p>

        <div className="max-w-2xl mx-auto space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground transition-all ${isHighlighted ? 'text-primary scale-110' : ''}`} />
              <Input ref={inputRef} type="text" placeholder={t('urlToAgent.urlPlaceholder')} value={url} onChange={(e) => setUrl(e.target.value)} onBlur={handleUrlBlur} onKeyDown={(e) => {
              if (e.key === 'Enter' && !isLoading) {
                handleAnalyze();
              }
            }} className={`pl-10 h-12 sm:h-14 text-base sm:text-lg bg-card border-2 transition-all duration-300 ${isHighlighted ? 'border-primary ring-4 ring-primary/20 animate-pulse' : 'border-border'}`} disabled={isLoading} />
            </div>
            <Button onClick={handleAnalyze} disabled={isLoading} className="h-12 sm:h-14 px-6 sm:px-8 bg-primary hover:bg-primary/90 text-primary-foreground text-base sm:text-lg w-full sm:w-auto">
              {isLoading ? <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  {t('onboarding.urlAnalyzer.analyzing')}
                </> : <>
                  <Sparkles className="mr-2 h-5 w-5" />
                  {t('onboarding.urlAnalyzer.analyze')}
                </>}
            </Button>
          </div>
          
          <p className="text-xs text-muted-foreground">
            {t('urlToAgent.inputHint')}
          </p>

          {isLoading &&
        <div className="py-8 md:py-12">
              <div className="max-w-md mx-auto">
                <div className="space-y-4">
                  {ANALYSIS_STEPS.map((step, index) => {
                const stepIndex = ANALYSIS_STEPS.findIndex((s) => s.id === currentStep);
                const isActive = step.id === currentStep;
                const isComplete = stepIndex > index || currentStep === 'complete';
                const isPending = stepIndex < index && currentStep !== 'complete';
                const StepIcon = step.icon;

                return (
                  <div
                    key={step.id}
                    className={`flex items-center gap-4 p-4 rounded-xl transition-all duration-500 ${
                    isActive ?
                    'bg-primary/10 border border-primary/30 scale-[1.02]' :
                    isComplete ?
                    'bg-green-500/10 border border-green-500/30' :
                    'bg-muted/30 border border-transparent opacity-50'}`
                    }>

                        <div className={`relative flex items-center justify-center w-12 h-12 rounded-full transition-all duration-500 ${
                    isActive ?
                    'bg-primary text-primary-foreground' :
                    isComplete ?
                    'bg-green-500 text-white' :
                    'bg-muted text-muted-foreground'}`
                    }>
                          {isComplete ?
                      <Check className="w-6 h-6 animate-in zoom-in duration-300" /> :
                      isActive ?
                      <>
                              <StepIcon className="w-6 h-6" />
                              <div className="absolute inset-0 rounded-full border-2 border-primary/50 animate-ping" />
                            </> :

                      <StepIcon className="w-5 h-5" />
                      }
                        </div>
                        
                        <div className="flex-1">
                          <p className={`font-medium transition-colors ${
                      isActive ?
                      'text-foreground' :
                      isComplete ?
                      'text-green-600 dark:text-green-400' :
                      'text-muted-foreground'}`
                      }>
                            {t(isSearchMode ? step.searchLabelKey : step.labelKey)}
                          </p>
                          {isActive &&
                      <p className="text-sm text-muted-foreground animate-pulse">
                              {t('urlToAgent.processing')}
                            </p>
                      }
                          {isComplete &&
                      <p className="text-sm text-green-600 dark:text-green-400">
                              {t('urlToAgent.complete')}
                            </p>
                      }
                        </div>
                        
                        {isActive &&
                    <Loader2 className="w-5 h-5 animate-spin text-primary" />
                    }
                      </div>);

              })}
                </div>
                
                {/* Progress bar */}
                <div className="mt-6 h-2 bg-muted rounded-full overflow-hidden">
                  <div
                className="h-full bg-gradient-to-r from-primary to-green-500 transition-all duration-700 ease-out rounded-full"
                style={{
                  width: currentStep === 'fetching' ? '33%' :
                  currentStep === 'analyzing' ? '66%' :
                  currentStep === 'building' ? '85%' :
                  currentStep === 'complete' ? '100%' :
                  '0%'
                }} />

                </div>
              </div>
            </div>
        }
        </div>

        


















      </div>
    </div>;
};
export default UrlAnalyzer;